import { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LogIn, LogOut, Download, Search, Users, Loader2, ShieldCheck, ArrowRight, Pencil, Trash2, Wallet, UserCheck } from "lucide-react";
import { API_BASE } from "@/lib/festival";
import { COMPETITIONS, WORKSHOPS } from "@/lib/festival";
import EditRegistrationModal from "@/components/EditRegistrationModal";

const TOKEN_KEY = "festival_admin_token";

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [me, setMe] = useState(null);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API_BASE}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setMe(r.data))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      });
  }, [token]);

  const onLogin = (t, user) => {
    localStorage.setItem(TOKEN_KEY, t);
    setToken(t);
    setMe(user);
  };
  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setMe(null);
  };

  return (
    <main className="min-h-screen bg-[#FFFDF0]" data-testid="admin-page">
      <div className="container-x py-10">
        <div className="flex items-center justify-between mb-8">
          <a href="/" className="flex items-center gap-2 text-sm font-black text-[#0066CC] hover:underline" data-testid="admin-back">
            <ArrowRight className="w-4 h-4" />
            العودة إلى الموقع
          </a>
          {me && (
            <button onClick={onLogout} className="btn-outline !py-2 !px-4 text-sm" data-testid="admin-logout">
              <LogOut className="w-4 h-4" /> تسجيل الخروج
            </button>
          )}
        </div>

        {!token || !me ? <LoginCard onLogin={onLogin} /> : <Dashboard token={token} me={me} />}
      </div>
    </main>
  );
}

function LoginCard({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_BASE}/auth/login`, { email, password });
      onLogin(data.access_token, { email: data.email, name: data.name });
      toast.success("مرحبًا بعودتك!");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "فشل تسجيل الدخول");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-md mx-auto mt-16 playful-card !p-8"
      data-testid="admin-login-card"
    >
      <div className="text-center mb-6">
        <div className="inline-flex w-14 h-14 rounded-2xl bg-[#0066CC] text-white items-center justify-center">
          <ShieldCheck className="w-7 h-7" />
        </div>
        <h1 className="font-display text-2xl font-black text-[#1E293B] mt-4">لوحة تحكم المشرف</h1>
        <p className="text-sm text-[#475569] font-semibold">أدخل بياناتك للاطّلاع على التسجيلات.</p>
      </div>

      <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
        <label className="block">
          <span className="text-sm font-black text-[#1E293B] mb-1 block">البريد الإلكتروني</span>
          <input
            type="email"
            required
            dir="ltr"
            className="w-full px-4 py-3 rounded-xl border border-[#E2E8F0] bg-[#FFFDF0] text-left font-semibold focus:border-[#0066CC] focus:outline-none"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="admin-email-input"
            placeholder="admin@libyafestival.ly"
          />
        </label>
        <label className="block">
          <span className="text-sm font-black text-[#1E293B] mb-1 block">كلمة المرور</span>
          <input
            type="password"
            required
            dir="ltr"
            className="w-full px-4 py-3 rounded-xl border border-[#E2E8F0] bg-[#FFFDF0] text-left font-semibold focus:border-[#0066CC] focus:outline-none"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input"
          />
        </label>
        <button type="submit" disabled={loading} className="btn-3d w-full justify-center" data-testid="admin-login-submit">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><LogIn className="w-5 h-5" /> دخول</>}
        </button>
      </form>
    </motion.div>
  );
}

function Dashboard({ token, me }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [activityFilter, setActivityFilter] = useState(""); // "" = all
  const [editRow, setEditRow] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchRows = () => {
    setLoading(true);
    axios
      .get(`${API_BASE}/registrations`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setRows(r.data))
      .catch(() => toast.error("تعذّر جلب التسجيلات"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onSaved = (updated) => {
    setRows((rs) => rs.map((r) => (r.id === updated.id ? updated : r)));
  };

  const toggleAttended = async (row) => {
    const newVal = !row.attended;
    // Optimistic update
    setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, attended: newVal } : r)));
    try {
      await axios.patch(
        `${API_BASE}/registrations/${row.id}`,
        { attended: newVal },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (e) {
      // Revert on error
      setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, attended: !newVal } : r)));
      toast.error("تعذّر تحديث حالة الحضور");
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${API_BASE}/registrations/${confirmDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows((rs) => rs.filter((r) => r.id !== confirmDelete.id));
      toast.success("تم حذف التسجيل");
    } catch (e) {
      toast.error("فشل الحذف");
    } finally {
      setConfirmDelete(null);
    }
  };

  const filtered = rows.filter((r) => {
    // Activity filter
    if (activityFilter) {
      const inComp = (r.competitions || []).includes(activityFilter);
      const inWork = (r.workshops || []).includes(activityFilter);
      if (!inComp && !inWork) return false;
    }
    // Text search filter
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return (
      r.full_name?.toLowerCase().includes(s) ||
      r.school_name?.toLowerCase().includes(s) ||
      r.area?.toLowerCase().includes(s) ||
      r.companion_phone?.toLowerCase().includes(s)
    );
  });

  const exportExcel = () => {
    const data = filtered.map((r, i) => ({
      "#": i + 1,
      "حضر": r.attended ? "نعم" : "لا",
      "اسم الطفل": r.full_name,
      "العمر": r.age,
      "المدرسة": r.school_name,
      "المنطقة": r.area,
      "المرافق": r.companion_name,
      "هاتف المرافق": r.companion_phone,
      "مرض صحي": r.health_issue ? "نعم" : "لا",
      "تفاصيل صحية": r.health_issue_details || "",
      "أمر نفسي": r.psych_issue ? "نعم" : "لا",
      "تفاصيل نفسية": r.psych_issue_details || "",
      "المسابقات": (r.competitions || []).join("، "),
      "الورش": (r.workshops || []).join("، "),
      "الرسوم (د.ل)": r.fee ?? "",
      "تاريخ التسجيل": r.created_at,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = Object.keys(data[0] || {}).map(() => ({ wch: 18 }));
    const wb = XLSX.utils.book_new();
    const sheetName = activityFilter ? activityFilter.slice(0, 30) : "التسجيلات";
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    const fnSuffix = activityFilter ? `-${activityFilter.replace(/\s+/g, "_")}` : "";
    XLSX.writeFile(wb, `festival-registrations${fnSuffix}-${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success(activityFilter ? `تم تصدير ${filtered.length} طفل — ${activityFilter}` : "تم تصدير الملف");
  };

  const totalKids = rows.length;
  const ages = rows.reduce((a, r) => a + (r.age || 0), 0);
  const avgAge = totalKids ? (ages / totalKids).toFixed(1) : "0";
  const totalRevenue = rows.reduce((a, r) => a + (r.fee || 0), 0);
  const attendedRows = rows.filter((r) => r.attended);
  const attendedCount = attendedRows.length;
  const attendedRevenue = attendedRows.reduce((a, r) => a + (r.fee || 0), 0);
  const withHealth = rows.filter((r) => r.health_issue).length;

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl font-black text-[#1E293B]">مرحبًا، {me.name}</h1>
          <p className="text-sm text-[#475569] font-semibold mt-1">لوحة تحكم تسجيلات المهرجان</p>
        </div>
        <button onClick={exportExcel} disabled={filtered.length === 0} className="btn-3d disabled:opacity-50" data-testid="export-excel-button">
          <Download className="w-5 h-5" />
          تصدير Excel
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8" data-testid="admin-stats">
        <StatCard label="إجمالي المسجّلين" value={totalKids} icon={Users} color="#0066CC" />
        <StatCard label="الحضور الفعلي" value={`${attendedCount}/${totalKids}`} icon={UserCheck} color="#10B981" />
        <StatCard label="الدخل المتوقّع" value={`${totalRevenue} د.ل`} icon={Wallet} color="#FFD600" />
        <StatCard label="الدخل الفعلي (حضور)" value={`${attendedRevenue} د.ل`} icon={Wallet} color="#E63946" />
        <StatCard label="حالات صحية" value={withHealth} icon={Users} color="#94A3B8" />
      </div>

      {/* Search + Activity Filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <input
            type="text"
            className="w-full pr-10 pl-4 py-3 rounded-xl border border-[#E2E8F0] bg-white focus:border-[#0066CC] focus:outline-none font-semibold"
            placeholder="ابحث بالاسم، المدرسة، المنطقة، أو الهاتف..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="admin-search-input"
          />
        </div>
        <select
          value={activityFilter}
          onChange={(e) => setActivityFilter(e.target.value)}
          className="px-4 py-3 rounded-xl border border-[#E2E8F0] bg-white focus:border-[#0066CC] focus:outline-none font-semibold text-right cursor-pointer"
          data-testid="admin-activity-filter"
        >
          <option value="">كل الأنشطة ({rows.length})</option>
          <optgroup label="المسابقات">
            {COMPETITIONS.map((c) => {
              const count = rows.filter((r) => (r.competitions || []).includes(c.label)).length;
              return (
                <option key={c.id} value={c.label}>
                  {c.label} ({count})
                </option>
              );
            })}
          </optgroup>
          <optgroup label="الورش">
            {WORKSHOPS.map((w) => {
              const count = rows.filter((r) => (r.workshops || []).includes(w.label)).length;
              return (
                <option key={w.id} value={w.label}>
                  {w.label} ({count})
                </option>
              );
            })}
          </optgroup>
        </select>
        {activityFilter && (
          <button
            onClick={() => setActivityFilter("")}
            className="btn-outline !py-2 !px-4 text-sm"
            data-testid="clear-activity-filter"
          >
            مسح الفلتر
          </button>
        )}
      </div>

      {activityFilter && (
        <div className="mb-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0066CC]/10 text-[#0066CC] text-xs font-black">
          يعرض {filtered.length} طفل مسجّل في «{activityFilter}»
        </div>
      )}

      {/* Table */}
      <div className="playful-card !p-0 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-[#475569]">
            <Loader2 className="w-6 h-6 animate-spin inline" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-[#475569] font-semibold" data-testid="admin-empty">
            لا توجد تسجيلات بعد.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-table">
              <thead className="bg-[#1E293B] text-[#FFD600] text-right">
                <tr>
                  <Th>حضر</Th>
                  <Th>#</Th>
                  <Th>الاسم</Th>
                  <Th>العمر</Th>
                  <Th>المدرسة</Th>
                  <Th>المنطقة</Th>
                  <Th>المرافق</Th>
                  <Th>الهاتف</Th>
                  <Th>صحي</Th>
                  <Th>نفسي</Th>
                  <Th>المسابقات</Th>
                  <Th>الورش</Th>
                  <Th>الرسوم</Th>
                  <Th>إجراءات</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr key={r.id} className={`border-t border-[#E2E8F0] hover:bg-[#FFFDF0] ${r.attended ? "bg-[#10B981]/5" : ""}`} data-testid={`row-${i}`}>
                    <Td>
                      <button
                        onClick={() => toggleAttended(r)}
                        className={`w-6 h-6 rounded-md flex items-center justify-center transition-colors ${
                          r.attended
                            ? "bg-[#10B981] text-white"
                            : "bg-white border-2 border-[#CBD5E1] hover:border-[#10B981]"
                        }`}
                        title={r.attended ? "حضر — اضغط للإلغاء" : "لم يحضر بعد"}
                        data-testid={`attended-toggle-${i}`}
                      >
                        {r.attended && <UserCheck className="w-4 h-4" />}
                      </button>
                    </Td>
                    <Td>{i + 1}</Td>
                    <Td className="font-black text-[#0066CC]">{r.full_name}</Td>
                    <Td>{r.age}</Td>
                    <Td>{r.school_name}</Td>
                    <Td>{r.area}</Td>
                    <Td>{r.companion_name}</Td>
                    <Td dir="ltr" className="text-left">{r.companion_phone}</Td>
                    <Td><Pill v={r.health_issue} /></Td>
                    <Td><Pill v={r.psych_issue} /></Td>
                    <Td className="max-w-[200px]"><span className="text-xs">{(r.competitions || []).join("، ")}</span></Td>
                    <Td className="max-w-[200px]"><span className="text-xs">{(r.workshops || []).join("، ")}</span></Td>
                    <Td>
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-black ${
                        r.fee === 35 ? "bg-[#E63946]/10 text-[#E63946]" : "bg-[#0066CC]/10 text-[#0066CC]"
                      }`}>
                        {r.fee ?? 50} د.ل
                      </span>
                    </Td>
                    <Td>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => setEditRow(r)}
                          className="w-8 h-8 rounded-lg bg-[#0066CC]/10 hover:bg-[#0066CC] text-[#0066CC] hover:text-white transition-colors flex items-center justify-center"
                          title="تعديل"
                          data-testid={`edit-btn-${i}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setConfirmDelete(r)}
                          className="w-8 h-8 rounded-lg bg-[#E63946]/10 hover:bg-[#E63946] text-[#E63946] hover:text-white transition-colors flex items-center justify-center"
                          title="حذف"
                          data-testid={`delete-btn-${i}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <EditRegistrationModal
        open={!!editRow}
        row={editRow}
        token={token}
        onClose={() => setEditRow(null)}
        onSaved={onSaved}
      />

      {confirmDelete && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="delete-confirm">
          <div className="absolute inset-0 bg-[#1E293B]/70 backdrop-blur-sm" onClick={() => setConfirmDelete(null)} />
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="relative bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 border-4 border-[#E63946]"
          >
            <div className="w-14 h-14 rounded-2xl bg-[#E63946]/10 text-[#E63946] mx-auto flex items-center justify-center">
              <Trash2 className="w-7 h-7" />
            </div>
            <h3 className="font-display text-xl font-black text-[#1E293B] text-center mt-4">
              تأكيد الحذف
            </h3>
            <p className="text-sm text-[#475569] font-semibold text-center mt-2">
              هل أنت متأكد من حذف تسجيل الطفل <span className="text-[#E63946] font-black">{confirmDelete.full_name}</span>؟
              لا يمكن التراجع عن هذا الإجراء.
            </p>
            <div className="mt-6 flex gap-3">
              <button onClick={() => setConfirmDelete(null)} className="btn-outline !py-2.5 flex-1 justify-center" data-testid="delete-cancel">
                إلغاء
              </button>
              <button
                onClick={doDelete}
                className="flex-1 py-2.5 rounded-full bg-[#E63946] hover:bg-[#c8303c] text-white font-black shadow-lg transition-colors"
                data-testid="delete-confirm-btn"
              >
                نعم، احذف
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="playful-card">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-black text-[#94A3B8]">{label}</div>
          <div className="font-display text-3xl font-black text-[#1E293B] mt-1">{value}</div>
        </div>
        <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white" style={{ backgroundColor: color }}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
}

function Th({ children }) {
  return <th className="px-3 py-3 text-right text-xs font-black uppercase tracking-wide">{children}</th>;
}
function Td({ children, className = "", ...rest }) {
  return <td className={`px-3 py-3 text-right font-semibold text-[#1E293B] ${className}`} {...rest}>{children}</td>;
}
function Pill({ v }) {
  return v ? (
    <span className="inline-flex px-2 py-1 rounded-full bg-[#E63946]/10 text-[#E63946] text-xs font-black">نعم</span>
  ) : (
    <span className="inline-flex px-2 py-1 rounded-full bg-[#10B981]/10 text-[#10B981] text-xs font-black">لا</span>
  );
}
